import { useEffect, useRef, useState } from "react";
import { cn } from "@apex/shared";

export interface SearchInputProps {
  /** Current value (controlled). */
  value: string;
  /**
   * Called with the new value AFTER the debounce window elapses. Bursts of
   * typing collapse into a single trailing call.
   */
  onChange: (value: string) => void;
  /** Placeholder, default "Search...". */
  placeholder?: string;
  /** Debounce window in ms, default 200. */
  debounceMs?: number;
  /** Additional class for the wrapping div. */
  className?: string;
  /** Optional aria-label for the input — falls back to the placeholder. */
  "aria-label"?: string;
}

/**
 * SearchInput — an `<input type="search">` with a leading magnifier icon, a
 * trailing clear-x button when non-empty, and a built-in trailing-edge
 * debounce on the parent's `onChange`. Used both on the Models step (#532)
 * and on the Configure step accessory list (Batch D).
 */
export function SearchInput({
  value,
  onChange,
  placeholder = "Search...",
  debounceMs = 200,
  className,
  ...rest
}: SearchInputProps) {
  // Local state so typing is responsive even before the debounce fires.
  const [draft, setDraft] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep local in sync when parent resets externally (e.g. clear-all).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setDraft(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange(next);
    }, debounceMs);
  }

  function handleClear() {
    setDraft("");
    if (timerRef.current) clearTimeout(timerRef.current);
    onChange("");
  }

  return (
    <div className={cn("apex-relative apex-flex-1", className)}>
      <span
        aria-hidden="true"
        className="apex-absolute apex-left-3 apex-top-1/2 apex--translate-y-1/2 apex-text-muted-foreground apex-pointer-events-none"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      </span>
      <input
        type="search"
        value={draft}
        onChange={handleInputChange}
        placeholder={placeholder}
        aria-label={rest["aria-label"] ?? placeholder}
        className={cn(
          "apex-flex apex-h-10 apex-w-full apex-rounded-md apex-border apex-border-input apex-bg-background apex-text-sm",
          "apex-pl-9 apex-pr-9 apex-py-2",
          "placeholder:apex-text-muted-foreground",
          "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring focus-visible:apex-ring-offset-2",
          // Hide the browser's built-in [type=search] cancel button — we draw our own
          "[&::-webkit-search-cancel-button]:apex-hidden"
        )}
      />
      {draft.length > 0 && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className={cn(
            "apex-absolute apex-right-2 apex-top-1/2 apex--translate-y-1/2",
            "apex-rounded apex-p-1 apex-text-muted-foreground hover:apex-text-foreground hover:apex-bg-muted",
            "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring"
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
